import * as React from "react";
import { action, computed, observable } from "mobx";
import { inject, observer } from "mobx-react";
import { RouterStore } from "mobx-react-router";
import autobind from "autobind-decorator";

import {
  Container,
  Paper,
  ListItemText,
  CircularProgress,
  IconButton,
  Toolbar,
  Typography,
  AppBar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  TextField,
  MenuItem,
  Snackbar,
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableCell,
  Tooltip,
  TableRow,
  TableSortLabel,
  ButtonGroup,
  Menu,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import DeleteIcon from "@mui/icons-material/Delete";
import LogoutButton from "../common/LogoutButton";
import AccountService, {
  CustomUserItem,
  UserItem,
} from "../../service/AccountService";
import EditIcon from "@mui/icons-material/Edit";
import { styled } from "@mui/material/styles";
import firebase from "firebase/compat/app";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PricingPlan from "../subscription/pricingPlan";
import { withStyles } from "@mui/styles";

const UserType = styled(ListItemText)({
  textAlign: "left",
  maxWidth: "220px",
  minWidth: "190px",
  marginRight: "20px",
  alignSelf: "center",
});
const UserDiv = styled(Container)({
  display: "flex",
  flexDirection: "row",
  width: "100%",
  "@media screen and (max-width: 590px)": {
    flexDirection: "column",
  },
});

const NavButton = withStyles({
  label: {
    color: "#FFF",
  },
})(Button);

const MenuNavButton = withStyles({
  label: {
    color: "#000",
  },
})(Button);

const CustomPaper = styled(Paper)({
  padding: "2rem",
  marginTop: "80px",
});

const BrowserHideDiv = styled("div")({
  "@media screen and (min-width: 720px)": {
    display: "none",
  },
});

export const StyledTableCell = withStyles({
  head: {
    backgroundColor: "rgb(196,196,196,.15)",
  },
})(TableCell);

export const StyledTableRow = withStyles({
  root: {
    borderBottomStyle: "solid",
    borderBottomColor: "rgb(0,0,0,0.18)",
    borderBottomWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: "rgb(0,0,0,0.18)",
    borderTopWidth: "1px",
  },
})(TableRow);

const userTypes = ["ADMIN", "FREE_USER", "STANDARD_USER", "PREMIUM_USER"];

type pageState = "LOADING" | "NORMAL";

type UserListStatus = "LOADING" | "NORMAL";

type Sort = "desc" | "asc";

type AdminProps = {
  routing?: RouterStore;
  account?: AccountService;
};

type AdminState = {
  inputVal: string;
  pageNum: number;
  customUserList: any[];
  prices: any[];
  lastDocument: any;
  orderBy: string;
  order: Sort;
};

@inject("routing")
@inject("account")
@observer
export default class AdminPage extends React.Component<AdminProps, AdminState> {
  @observable
  private _status: UserListStatus = "NORMAL";

  @observable
  private _deletingUser: CustomUserItem = null;

  @observable
  private _isEditUserType: boolean = false;

  @observable
  private _isDeleteUser: boolean = false;

  @observable
  private _priceStatus: pageState = "LOADING";

  @observable
  private _selectedUser: CustomUserItem = null;

  @observable
  private _selectedUserType: string = null;

  @observable
  snackMessage = "";

  @observable
  private anchorElMain = null;

  @observable
  private _navType = "lists_of_users";

  state: AdminState = {
    inputVal: "",
    pageNum: 1,
    customUserList: [],
    prices: [],
    lastDocument: null,
    orderBy: "createdAt",
    order: "desc",
  };

  @autobind
  @action
  handleSignoutAdmin() {
    this.props.account.handleLogout();
  }
  @autobind
  handleMoreClose() {
    this.anchorElMain = null;
  }

  @autobind
  async deleteUser() {
    const lastDoc = this.props.account.adminData
      ? this.props.account.adminData[this.props.account.adminData.length - 1]
      : null;
    await this.props.account.handleDeleteUser(this._deletingUser.uid);
    await this.props.account.reloadAdminData(this.state.pageNum, lastDoc);
    const { customUserData, lastDocument } = await this.getCustomUserData();
    this.setState({
      customUserList: customUserData,
      lastDocument,
    });

    this._deletingUser = null;
    this._isDeleteUser = false;
  }

  handleMoreClick(event: React.MouseEvent<HTMLButtonElement>) {
    this.anchorElMain = event.currentTarget;
  }

  @autobind
  handleDeleteUserNo() {
    this._deletingUser = null;
    this._isDeleteUser = false;
  }

  handleDeleteUserClick(user: CustomUserItem) {
    this._deletingUser = user;
    this._isDeleteUser = true;
  }

  @action
  editUserType(user: CustomUserItem) {
    this._selectedUser = user;
    // this._selectedUserType = user.userData.userType;
    this._selectedUserType = user.userType;
    this._isEditUserType = true;
  }

  @autobind
  async saveUserTypeChange() {
    const lastDoc = this.props.account.adminData
      ? this.props.account.adminData[this.props.account.adminData.length - 1]
      : null;
    let result = await this.props.account.updateUserType(
      this._selectedUser.uid,
      this._selectedUserType
    );
    if (result === "SUCCESS") {
      this.snackMessage = "SUCCESS: Updated User type!";
      await this.props.account.reloadAdminData(this.state.pageNum, lastDoc);
      const { customUserData, lastDocument } = await this.getCustomUserData();
      this.setState({
        customUserList: customUserData,
        lastDocument,
      });
      this._isEditUserType = false;
    } else {
      this.snackMessage = result.toString();
    }
  }

  renderNavBar() {
    return (
      <AppBar position="static">
        <Toolbar variant="dense">
          {/* <Avatar style={{backgroundColor: '#3f50b5'}}>
                        <Icon icon={robotIcon} />
                    </Avatar> */}
          <Typography variant="h6" style={{ flexGrow: 1, color: "inherit" }}>
            Profile
          </Typography>
          <LogoutButton />
        </Toolbar>
      </AppBar>
    );
  }

  handleRequestSort(sortBy: string) {
    if (this.state.orderBy == sortBy) {
      this.setState({
        order: this.state.order == "asc" ? "desc" : "asc",
        customUserList: [],
      });
    } else {
      this.setState({
        orderBy: sortBy,
        customUserList: [],
      });
    }

    // const userDataSorted = this.state.customUserList.sort((a, b) => {
    //   var value: number;
    //   if (typeof a[sortBy] == "string") {
    //     value = a[sortBy].localeCompare(b[sortBy]);
    //   } else {
    //     value = a[sortBy] - b[sortBy];
    //   }
    //   let map = {
    //     asc: 1,
    //     desc: -1,
    //   };

    //   return value * map[this.state.order];
    // });

    // this.setState({
    //   customUserList: userDataSorted,
    // });
  }

  searchHandler(): any[] {
    const newUserList = this.state.customUserList.filter((user) =>
      user.email.toLocaleLowerCase().includes(this.state.inputVal)
    );
    return newUserList;
  }
  pageChangeHandler = (event: React.ChangeEvent<unknown>, value: number) => {
    event.preventDefault();
    window.scrollTo(0, 0);
    this.setState({ pageNum: value, customUserList: [] });
  };

  switchTypeHandler(type: string) {
    this._navType = type;
  }

  @autobind
  async fetchPrices() {
    const getData = await firebase.firestore().collection("prices").get();
    const getPrices = getData.docs.map((plan) => plan.data());
    return getPrices;
  }

  @autobind
  async getCustomUserData() {
    const { documents, lastDocument } = await this.props.account.getDataByPage(
      this.state.pageNum,
      this.state.lastDocument,
      this.state.inputVal,
      this.state.orderBy,
      this.state.order
    );
    const customUserData = documents.map((user) => {
      const { email, userType, createdAt } = user.userData;
      const { uid } = user;
      return {
        uid,
        email,
        userType,
        createdAt: createdAt,
      };
    });
    return { customUserData, lastDocument };
  }

  async componentDidMount() {
    const prices = await this.fetchPrices();
    const { customUserData, lastDocument } = await this.getCustomUserData();
    // this.handleRequestSort("createdAt");
    this.setState({
      customUserList: customUserData,
      lastDocument,
      prices,
    });
  }

  async componentDidUpdate(
    prevProps: Readonly<AdminProps>,
    prevState: Readonly<AdminState>,
    snapshot?: any
  ): Promise<void> {
    if (
      prevState.pageNum !== this.state.pageNum ||
      prevState.inputVal !== this.state.inputVal ||
      prevState.orderBy !== this.state.orderBy ||
      prevState.order !== this.state.order
    ) {
      const { customUserData, lastDocument } = await this.getCustomUserData();
      // this.handleRequestSort("createdAt");
      this.setState({
        customUserList: customUserData,
        lastDocument: lastDocument,
      });
    }

    // if (!this.state.inputVal && this.state.orderBy === "email") {
    //   this.setState({
    //     ...this.state,
    //     orderBy: "createdAt",
    //   });
    // }
  }

  @autobind
  renderPricingValues() {
    return (
      <>
        <CustomPaper>
          {this.state.prices.length > 0 ? (
            <>
              <PricingPlan
                name="Business Plan"
                pricesData={this.state.prices}
              />
              <PricingPlan name="Premium Plan" pricesData={this.state.prices} />
            </>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "187px",
              }}
            >
              <CircularProgress />
            </div>
          )}
        </CustomPaper>
      </>
    );
  }

  renderContents() {
    const { pageNum } = this.state;
    // const usersListRange = this.searchHandler().slice(
    //   25 * (pageNum - 1),
    //   25 * pageNum
    // );
    if (this.state.customUserList.length == 0) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "720px",
          }}
        >
          <CircularProgress />
        </div>
      );
    }
    return (
      <TableContainer component={Paper} style={{ marginBottom: "20px" }}>
        <Table style={{ maxHeight: "800px" }}>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell></StyledTableCell>
              <StyledTableCell align="left">
                <TableSortLabel
                  active={this.state.orderBy === "email"}
                  direction={
                    this.state.orderBy === "email" ? this.state.order : "asc"
                  }
                  onClick={() => this.handleRequestSort("email")}
                  style={{ minWidth: "120px" }}
                >
                  User Email
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell>
                <TableSortLabel
                  active={this.state.orderBy === "userType"}
                  direction={
                    this.state.orderBy === "userType" ? this.state.order : "asc"
                  }
                  onClick={() => this.handleRequestSort("userType")}
                  style={{ minWidth: "120px" }}
                >
                  User Type
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="right">
                <TableSortLabel
                  active={this.state.orderBy === "createdAt"}
                  direction={
                    this.state.orderBy === "createdAt"
                      ? this.state.order
                      : "asc"
                  }
                  onClick={() => this.handleRequestSort("createdAt")}
                  style={{ minWidth: "120px" }}
                >
                  Created On
                </TableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {this.state.customUserList.map((user, index) => (
              <StyledTableRow key={`key-${user.uid}`}>
                <TableCell align="left">
                  <AccountBoxIcon />
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.userType}</TableCell>
                <TableCell align="right">
                  {new Date(user.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell align="center" style={{ width: "270px" }}>
                  <IconButton onClick={() => this.editUserType(user)}>
                    <EditIcon />
                  </IconButton>
                  {this._deletingUser?.uid == user.uid ? (
                    <CircularProgress size={18} />
                  ) : (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      disabled={this._deletingUser != null}
                      onClick={() => this.handleDeleteUserClick(user)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination
          count={Math.trunc(4800 / 25 + 1)}
          color="primary"
          style={{
            margin: "20px auto",
            width: "fit-content",
          }}
          onChange={this.pageChangeHandler}
          page={this.state.pageNum}
        />
      </TableContainer>
    );
  }

  @autobind
  async updateAllUsersHandler() {
    // get all Users
    const result = await firebase
      .firestore()
      .collection("userx")
      .where("userType", "==", "PREMIUM_USER")
      .get();
    const users = result.docs.map((doc) => {
      return { uid: doc.id, data: doc.data() };
    });
    users.forEach(async (user) => {
      const { uid, data } = user;
      await firebase.firestore().collection("userx").doc(uid).set(
        {
          userType: "FREE_USER",
        },
        { merge: true }
      );
    });
    // new object + new value (free account : true)
    // update all users
  }

  render() {
    return (
      <React.Fragment>
        <div
          style={{
            width: "100%",
            height: "env(safe-area-inset-top)",
            backgroundColor: "#3f51b5",
          }}
        />
        <AppBar position="static">
          <Toolbar variant="dense">
            <Typography variant="h6" style={{ flexGrow: 1, color: "inherit" }}>
              FlowCharts
            </Typography>
            {/* <IconButton
              color="inherit"
              onClick={this.updateAllUsersHandler}
              style={{ fontSize: '1.2rem' }}
            >
              Upgrade All Users
            </IconButton> */}
            <IconButton
              color="inherit"
              onClick={() => this.switchTypeHandler("lists_of_users")}
              style={{ fontSize: "1.2rem" }}
            >
              Lists of Users
            </IconButton>
            <IconButton
              color="inherit"
              onClick={() => this.switchTypeHandler("pricing_values")}
              style={{ fontSize: "1.2rem" }}
            >
              Pricing Values
            </IconButton>
            <BrowserHideDiv>
              <IconButton
                aria-label="more"
                aria-controls="long-menu"
                aria-haspopup="true"
                onClick={(e) => this.handleMoreClick(e)}
              >
                <MoreVertIcon style={{ color: "#FFF" }} />
              </IconButton>
              <Menu
                id="main-menu"
                anchorEl={this.anchorElMain}
                keepMounted
                open={Boolean(this.anchorElMain)}
                onClose={this.handleMoreClose}
              >
                <MenuItem onClick={this.handleMoreClose}>
                  <MenuNavButton
                    aria-label="menu"
                    onClick={() => this.switchTypeHandler("lists_of_users")}
                  >
                    <AccountBoxIcon style={{ marginRight: "10px" }} />
                    Lists of Users
                  </MenuNavButton>
                </MenuItem>
                <MenuItem onClick={this.handleMoreClose}>
                  <MenuNavButton
                    aria-label="menu"
                    onClick={this.updateAllUsersHandler}
                  >
                    <AccountBoxIcon style={{ marginRight: "10px" }} />
                    UpgradeAllUsersHandler
                  </MenuNavButton>
                </MenuItem>

                <MenuItem onClick={this.handleMoreClose}>
                  <MenuNavButton
                    aria-label="menu"
                    onClick={() => this.switchTypeHandler("pricing_values")}
                  >
                    <AccountBoxIcon style={{ marginRight: "10px" }} />
                    Pricing Values
                  </MenuNavButton>
                </MenuItem>
              </Menu>
            </BrowserHideDiv>
            <input
              id="searchInput"
              placeholder="Search"
              onChange={(e) =>
                this.setState({
                  inputVal: e.target.value,
                  orderBy: "email",
                  pageNum: 1,
                })
              }
              value={this.state.inputVal}
              style={{
                margin: "10px 20px",
                padding: "8px 24px",
                outline: "none",
                borderRadius: "4px",
                border: "1px solid transparent",
                color: "#000",
              }}
            />
            <LogoutButton />
          </Toolbar>
        </AppBar>
        <Container component="main" style={{ marginTop: "80px" }} maxWidth="lg">
          {this._navType === "lists_of_users" && (
            <Paper>{this.renderContents()}</Paper>
          )}

          {this._navType === "pricing_values" && this.renderPricingValues()}
        </Container>
        {this._isEditUserType ? (
          <Dialog
            open={this._isEditUserType}
            onClose={() => {
              this._isEditUserType = false;
            }}
            aria-labelledby="form-dialog-title"
          >
            <DialogTitle id="form-dialog-title">Select User Plan</DialogTitle>
            <DialogContent>
              <DialogContentText>{this._selectedUser.email}</DialogContentText>
              <TextField
                id="selectUserType"
                select
                label="User Type"
                value={this._selectedUserType}
                onChange={(e) => (this._selectedUserType = e.target.value)}
                fullWidth
              >
                {userTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => (this._isEditUserType = false)}
                color="primary"
              >
                Cancel
              </Button>
              <Button onClick={this.saveUserTypeChange} color="primary">
                Save User Plan
              </Button>
            </DialogActions>
          </Dialog>
        ) : null}

        <Dialog
          open={this._isDeleteUser}
          onClose={() => (this._isDeleteUser = false)}
        >
          <DialogTitle>Are you sure you want to delete the user?</DialogTitle>
          <DialogContent>{this._deletingUser?.email}</DialogContent>
          <DialogActions>
            <Button onClick={this.handleDeleteUserNo} color="primary">
              No
            </Button>
            <Button onClick={this.deleteUser} color="primary">
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={this.snackMessage ? true : false}
          autoHideDuration={6000}
          onClose={() => (this.snackMessage = null)}
        >
          <div
            style={{
              backgroundColor: "orange",
              color: "white",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            {this.snackMessage}
          </div>
          {/* <MuiAlert elevation={6} variant="filled" severity="info" onClose={()=>this.snackMessage=null}>{this.snackMessage}</MuiAlert> */}
        </Snackbar>
      </React.Fragment>
    );
  }
}
